import { logger, Logger } from '@4lch4/logger'
import Axios, { AxiosInstance, AxiosResponse } from 'axios'
import {
  IAppConfig,
  IBaseQueryParameters,
  IBaseResponseData,
  IRequestConfig,
  IResponse
} from '../interfaces/index.js'
import { DEFAULT_APP_CONFIG } from './constants/Defaults.js'

export class BaseRoute {
  //#region Properties
  /** An instance of my logger utility so each route has access. */
  protected logger: Logger = logger

  /** The object containing the config properties used by the library. */
  protected config: IAppConfig

  /**
   * The instance of Axios that is used for sending requests. It's a private
   * property so that it can't be modified by any of the routes which extend
   * this class.
   */
  private client: AxiosInstance
  //#endregion Properties

  //#region Private Methods
  constructor(config: IAppConfig) {
    this.config = { ...DEFAULT_APP_CONFIG, ...config }

    // If the `debug` is enabled then output the config settings.
    if (config.debug) {
      this.logger.info('[BaseRoute#constructor]: Setting this.config to the following...')
      this.logger.info(JSON.stringify(this.config, null, 2))
    }

    this.client = Axios.create({
      baseURL: config.baseUrl || DEFAULT_APP_CONFIG.baseUrl,
      headers: {
        'content-type': 'application/json',
        accept: 'application/json'
      }
    })
  }

  /**
   * Displays all of the usual content of the response object to the console.
   * This includes the `config`, `data`, `headers`, `request`, `status`, and
   * `statusText` properties.
   *
   * @param res The response object to display.
   */
  private displayResponseObject(res: any) {
    this.logger.info(`res.config ...`)
    this.logger.info(JSON.stringify(res.config, null, 2))

    this.logger.info(`res.data ...`)
    this.logger.info(JSON.stringify(res.data, null, 2))

    this.logger.info(`res.headers ...`)
    this.logger.info(JSON.stringify(res.headers, null, 2))

    this.logger.info(`res.request ...`)
    this.logger.info(JSON.stringify(res.request, null, 2))

    this.logger.info(`res.status ...`)
    this.logger.info(res.status)

    this.logger.info(`res.statusText ...`)
    this.logger.info(res.statusText)
  }

  /**
   * Creates an object containing, at least, the `api_key` and `format`
   * properties from the `config` object, and then adds any additional values
   * from the user provided `params` object with any duplicate values replacing
   * the existing values.
   *
   * @param params The optional params object provided by the user.
   * @returns An object containing, at least, the API key and format for the request.
   */
  private getRequestQueryParams(params?: IBaseQueryParameters): IBaseQueryParameters {
    const queryParams: IBaseQueryParameters = {
      // Default set `api_key` to the value of the `apiKey` property.
      api_key: this.config.apiKey,

      // Default set `format` to the value of the `responseFormat` property.
      format: this.config.responseFormat,

      // Apply any values from the `params` parameter and override previously set values.
      ...params
    }

    // If the `debug` flag is enabled then output the request params.
    if (this.config.debug) {
      this.logger.info('[BaseRoute#getRequestQueryParams]: queryParams...')
      this.logger.info(JSON.stringify(queryParams, null, 2))
    }

    return queryParams
  }

  /**
   * Creates and returns an IRequestConfig object, which extends
   * `AxiosRequestConfig`, using the provided parameters
   *
   * @param method The HTTP method to use for the request.
   * @param endpoint The endpoint to send the request to.
   * @param queryParams Any additional parameters to send with the request.
   *
   * @returns The IRequestConfig object to pass to Axios for the request.
   */
  private getRequestOptions(
    method: string,
    endpoint: string,
    queryParams?: IBaseQueryParameters
  ): IRequestConfig {
    const requestOptions: IRequestConfig = {
      params: this.getRequestQueryParams(queryParams),
      url: endpoint,
      method
    }

    return requestOptions
  }

  /**
   * Determines what kind of data to return, by checking the value of the
   * `responseFormat` property of the `config` object, and then returns the
   * response data in the appropriate format based on the following:
   *
   * If the `trimmedResponses` property of the `config` object is set to `true`
   * (the default value) then only the `data`, `status`, and
   * `statusText` properties of the response are returned. Otherwise, the
   * entire response object is returned.
   *
   * **_NOTE: The `D` generic type is used to specify the type of data that is
   * expected to be returned from the API._**
   *
   * @param res The AxiosResponse object returned from the API.
   *
   * @returns The response data from the API in the format specified in the app config.
   */
  private returnResponse<D = IBaseResponseData>(res: AxiosResponse): IResponse<D> {
    if (this.config.trimmedResponses) {
      const trimmedData = {
        data: res.data as D,
        status: res.status,
        statusText: res.statusText
      }

      // If the `debug` flag is enabled then output the trimmed response data.
      if (this.config.debug) {
        this.logger.info('[BaseRoute#returnResponse]: Returning the following object...')
        this.logger.info(JSON.stringify(trimmedData, null, 2))
      }

      return trimmedData
    } else return res
  }
  //#endregion Private Methods

  //#region Public Methods
  /**
   * Create and return the full path to a given endpoint, including any other
   * provided URL parameters (`urlParams`).
   *
   * For example, to get the path for something like `/bill/116/hr/1` then
   * provide the following values:
   *
   * - `endpoint` — `/bill`
   * - `urlParams` — `[115, 'hr', 1]`
   *
   * @param endpoint The base of the endpoint to send the request to.
   * @param pathParams The optional URL parameters to send with the request.
   *
   * @returns A string containing the full endpoint.
   */
  public getRequestEndpoint(
    endpoint: string,
    pathParams?: Array<string | number | undefined>
  ): string {
    // Initialize the endpointParts array with the provided endpoint string.
    const endpointParts = [endpoint]

    // If the urlParams parameter is provided then add each value that is not
    // undefined to the endpointParts array.
    if (pathParams) pathParams.forEach(param => (param ? endpointParts.push(`${param}`) : null))

    // If the `debug` flag is enabled, then output the endpoint that will be returned.
    if (this.config.debug) {
      this.logger.info(`[BaseRoute#getRequestEndpoint]: Returning ${endpointParts.join('/')}`)
    }

    // Join all parts together with a `/` and return the result.
    return endpointParts.join('/')
  }

  /**
   * Sends a request to the Congress API using the provided values and then
   * (depending on the value of the `trimmedResponses` property of the `config`
   * object) returns the response data or the entire response object.
   *
   * The `D` generic type is used to specify the type of data that is expected
   * to be returned from the API. For example, if you're making a request to the
   * `bills` endpoint then you would use `IBill[]` as the type.
   *
   * @param method The HTTP method to use for the request.
   * @param endpoint The endpoint to send the request to.
   * @param opts The optional path and query params that can modify the request.
   *
   * @returns The response from the API.
   */
  public async sendRequest<D = IBaseResponseData>(
    method: string,
    endpoint: string,
    opts?: IBaseQueryParameters
  ): Promise<IResponse<D>> {
    // If the `debug` flag is enabled, log the endpoint to the console.
    if (this.config.debug) {
      this.logger.info(
        `[BaseRoute#sendRequest]: Sending "${method}" request to this endpoint: ${endpoint}`
      )
    }

    // Generate the object to be used as the request config passed to `Axios`.
    const requestOptions = this.getRequestOptions(method, endpoint, opts)

    // If the `debug` flag is enabled then output the request options.
    if (this.config.debug) {
      this.logger.info('[BaseRoute#sendRequest]: requestOptions...')
      this.logger.info(JSON.stringify(requestOptions, null, 2))
    }

    // Send the request to the API and store the response as `res`.
    const res = await this.client.request(requestOptions)

    // Verify the `debug` property is set to `true` in the `config` object. If
    // so, display the response object.
    if (this.config.debug) this.displayResponseObject(res)

    return this.returnResponse(res)
  }
  //#endregion Public Methods
}
